import { z } from 'zod';
import { assemblyDayLogResponseSchema, assemblyDayLogTotalsSchema } from './assembly-day-log.schema';
import { assemblyReportItemResponseSchema, assemblyReportPhotoResponseSchema } from './assembly-report-item.schema';
import { REPORT_FIELDS, type ReportField } from './assembly-report.fields';

/**
 * Zod-Schemas des Baustellenberichts. Die Feldregeln werden aus der
 * Felddefinition **abgeleitet** — ein neues Feld dort ist damit automatisch
 * validiert (Muster: `stepFields` im QM-Schema, nur generisch statt handgetippt).
 */

export const assemblyReportStatusSchema = z.enum(['ENTWURF', 'FERTIG']);
export type AssemblyReportStatus = z.infer<typeof assemblyReportStatusSchema>;

/** Zod-Regel für ein einzelnes Feld — jedes Feld ist optional und löschbar. */
function ruleFor(field: ReportField): z.ZodTypeAny {
  const values = field.options?.map((option) => option.value) ?? [];
  switch (field.type) {
    case 'longtext':
      return z.string().trim().max(10000).nullable();
    case 'text':
      return z.string().trim().max(500).nullable();
    case 'number':
      // `Int`-Spalten müssen hier hart sein, sonst wirft erst Prisma (500 statt 422).
      return (field.isInteger ? z.number().int() : z.number()).finite().nullable();
    case 'date':
      return z.string().datetime().nullable();
    case 'bool3':
      return z.boolean().nullable();
    case 'enum':
      // Kein z.enum(): die Werte stehen erst zur Laufzeit fest (Felddefinition).
      return z
        .string()
        .refine((value) => values.includes(value), { message: 'Unbekannte Auswahl.' })
        .nullable();
    case 'multi':
      return z
        .array(z.string().refine((value) => values.includes(value), { message: 'Unbekannte Auswahl.' }))
        .max(values.length);
  }
}

const fieldRules = Object.fromEntries(REPORT_FIELDS.map((field) => [field.key, ruleFor(field)]));

/**
 * Adresse, an die ein Bau-Tagesbericht per Knopf verschickt wird (ADR 0076).
 * Bewusst KEIN Berichtsfeld aus `REPORT_FIELDS` — es sind Versand-Metadaten,
 * kein Inhalt des Dokuments, und sollen deshalb nicht im PDF auftauchen.
 */
const recipientEmailRule = z.string().trim().toLowerCase().max(200).email().nullable();

/**
 * Bericht aktualisieren — alle Felder optional (`.partial()`), damit die
 * Oberfläche je Feld einzeln speichern kann (Auto-Save).
 *
 * `recipientEmail` wird über `.merge()` statt Objekt-Spread ergänzt — ein
 * Objekt-Spread von `fieldRules` (Indexsignatur, da `Object.fromEntries`)
 * zusammen mit einem literalen Schlüssel lässt TypeScript die Indexsignatur
 * beim Typ-Inferenzieren von `z.object()` fallen (nur `recipientEmail` bliebe
 * übrig). `.merge()` ist der von Zod dafür vorgesehene Weg und bleibt stabil.
 */
export const updateAssemblyReportSchema = z
  .object(fieldRules)
  .partial()
  .merge(z.object({ recipientEmail: recipientEmailRule }).partial());
export type UpdateAssemblyReportInput = z.infer<typeof updateAssemblyReportSchema>;

/**
 * Projektdaten, die der Bericht anzeigt statt sie zu duplizieren. Solange der
 * Bericht ein Entwurf ist, kommen sie live aus dem Projekt; beim Fertigstellen
 * werden sie eingefroren.
 */
export const assemblyReportProjectDataSchema = z.object({
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  projectName: z.string().nullable(),
  customerName: z.string().nullable(),
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  projectManager: z.string().nullable(),
  location: z.string().nullable(),
  country: z.string().nullable(),
  plantType: z.string().nullable(),
  product: z.string().nullable(),
  baseDesign: z.string().nullable(),
  projectGroup: z.string().nullable(),
  basinCount: z.number().int().nullable(),
  lengthM: z.number().nullable(),
  widthM: z.number().nullable(),
  heightM: z.number().nullable(),
  waterLevelM: z.number().nullable(),
});
export type AssemblyReportProjectData = z.infer<typeof assemblyReportProjectDataSchema>;

/** Werte der eigenen Berichtsfelder — Schlüssel wie in der Felddefinition. */
export const assemblyReportValuesSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()]),
);
/** Werte-Map des Berichts — Formular und Ausfüllstand arbeiten damit. */
export type AssemblyReportValues = z.infer<typeof assemblyReportValuesSchema>;

export const assemblyReportResponseSchema = z.object({
  id: z.string().uuid(),
  assemblyTaskId: z.string().uuid(),
  /** Titel und Zeitraum des Einsatzes — Kopfzeile der Seite und des PDF. */
  taskTitle: z.string(),
  plannedStart: z.string().datetime().nullable(),
  plannedEnd: z.string().datetime().nullable(),
  technicianNames: z.array(z.string()),
  /**
   * Name der beauftragten Montage-Partnerfirma (ADR 0085/0087), `null` = keine.
   * Steht hier, damit die Unterlagen-Karte am Bericht sagen kann, WER die
   * freigegebenen Dateien zu sehen bekommt — eine Sichtbarkeit ohne Namen ist
   * keine Auskunft.
   */
  partnerName: z.string().nullable(),
  status: assemblyReportStatusSchema,
  finalizedAt: z.string().datetime().nullable(),
  /** Adresse für den Mail-Versand des Bau-Tagesberichts (ADR 0076), kein Berichtsfeld. */
  recipientEmail: z.string().nullable(),
  /** Live aus dem Projekt (ENTWURF) bzw. eingefroren (FERTIG). */
  project: assemblyReportProjectDataSchema,
  /** true, wenn `project` aus dem eingefrorenen Schnappschuss stammt. */
  isProjectDataFrozen: z.boolean(),
  values: assemblyReportValuesSchema,
  /** Baustellenverlauf, nach Tag sortiert (Schritt 3). */
  days: z.array(assemblyDayLogResponseSchema),
  /** Summen über den Verlauf — gerechnet, nie getippt (§6). */
  dayTotals: assemblyDayLogTotalsSchema,
  /** Listen der Blöcke I/J: Störungen, fehlende Teile, zu Erledigendes … (Schritt 5). */
  items: z.array(assemblyReportItemResponseSchema),
  /** Baustellenfotos — Bytes liegen im Datei-Speicher, hier nur die Metadaten. */
  photos: z.array(assemblyReportPhotoResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type AssemblyReportResponse = z.infer<typeof assemblyReportResponseSchema>;

/** Antwort auf „Bau-Tagesbericht per E-Mail senden" (ADR 0076). */
export const sendAssemblyDayReportEmailResponseSchema = z.object({
  sentTo: z.string(),
  sentAt: z.string().datetime(),
});
export type SendAssemblyDayReportEmailResponse = z.infer<typeof sendAssemblyDayReportEmailResponseSchema>;
